import * as React from "react"
import { useState, useEffect, useRef, useCallback } from "react"
import { motion, AnimatePresence } from "motion/react"
import {
  MessageCircle,
  Send,
  Smile,
  ChevronUp,
  ChevronDown,
  Info,
  Trophy,
  X,
  Settings,
} from "lucide-react"

// ---- Types ----
interface ChatMessage {
  id: string
  type: "player" | "dealer" | "system"
  playerName?: string
  text: string
  timestamp: number
}

// ---- Chat Overlay (Bottom-Left) ----
export function ChatOverlay({
  actionLog,
  roomName,
  onSendChat,
  incomingChats,
}: {
  actionLog: string[]
  roomName: string
  onSendChat?: (text: string) => void
  incomingChats?: { playerName: string; text: string; timestamp: number }[]
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState("")
  const [isOpen, setIsOpen] = useState(true)
  const [showEmoji, setShowEmoji] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const prevLogLenRef = useRef(0)

  // Convert action log entries to dealer messages
  useEffect(() => {
    if (actionLog.length > prevLogLenRef.current) {
      const newEntries = actionLog.slice(prevLogLenRef.current)
      const newMsgs: ChatMessage[] = newEntries.map((text, i) => ({
        id: `dealer-${Date.now()}-${i}`,
        type: "dealer" as const,
        text,
        timestamp: Date.now(),
      }))
      setMessages((prev) => [...prev, ...newMsgs].slice(-100))
    }
    prevLogLenRef.current = actionLog.length
  }, [actionLog])

  // Convert incoming player chats to messages
  const prevChatLenRef = useRef(0)
  useEffect(() => {
    const chats = incomingChats || []
    if (chats.length > prevChatLenRef.current) {
      const newChats = chats.slice(prevChatLenRef.current)
      const newMsgs: ChatMessage[] = newChats.map((c, i) => ({
        id: `remote-${Date.now()}-${i}`,
        type: "player" as const,
        playerName: c.playerName,
        text: c.text,
        timestamp: c.timestamp,
      }))
      setMessages((prev) => [...prev, ...newMsgs].slice(-100))
    }
    prevChatLenRef.current = chats.length
  }, [incomingChats])

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages])

  const sendMessage = () => {
    if (!input.trim()) return
    const text = input.trim()
    if (onSendChat) {
      onSendChat(text)
    }
    // Also add locally as "You" immediately for responsiveness
    setMessages((prev) => [
      ...prev,
      {
        id: `player-${Date.now()}`,
        type: "player",
        playerName: "You",
        text,
        timestamp: Date.now(),
      },
    ].slice(-100))
    setInput("")
    setShowEmoji(false)
  }

  const emojis = ["👍", "😂", "🔥", "💰", "😎", "🃏", "♠️", "♥️", "♦️", "♣️", "🎰", "😤", "🤑", "😱", "👏", "💀"]

  return (
    <div className="absolute bottom-2 left-2 z-30 w-[320px]" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {/* Toggle Button */}
      {!isOpen && (
        <motion.button
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-2 px-3 py-2 bg-[#1C1C32]/80 border border-[#2A2A45] rounded-xl backdrop-blur-md text-white/70 text-sm font-bold hover:text-white transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <MessageCircle className="w-4 h-4" />
          Chat
        </motion.button>
      )}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="bg-[#121220]/85 border border-[#2A2A45] rounded-2xl backdrop-blur-md overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#2A2A45]/60">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-[#F37020]" />
                <span className="text-white/90 text-xs font-bold uppercase tracking-wider">Chat</span>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              >
                <ChevronDown className="w-4 h-4 text-white/50" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="h-[180px] overflow-y-auto px-3 py-2 space-y-1.5 scrollbar-thin scrollbar-thumb-[#2A2A45] scrollbar-track-transparent">
              {messages.length === 0 && (
                <p className="text-white/20 text-xs text-center py-8">No messages yet</p>
              )}
              {messages.map((msg) => (
                <div key={msg.id} className="text-xs leading-relaxed">
                  {msg.type === "dealer" ? (
                    <div className="flex gap-1.5 items-baseline">
                      <span className="text-white/20 shrink-0" style={{ fontSize: "9px" }}>
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                      <span className="text-[#32CD32] font-bold shrink-0">Dealer »</span>
                      <span className="text-[#32CD32]/80">{msg.text}</span>
                    </div>
                  ) : msg.type === "system" ? (
                    <span className="text-[#FFD700]/60 italic">{msg.text}</span>
                  ) : (
                    <div className="flex gap-1.5 items-baseline">
                      <span className="text-white/20 shrink-0" style={{ fontSize: "9px" }}>
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                      <span className="font-bold shrink-0" style={{ color: "#F37020" }}>
                        {msg.playerName}:
                      </span>
                      <span className="text-white/80">{msg.text}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Emoji Picker */}
            <AnimatePresence>
              {showEmoji && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-t border-[#2A2A45]/60 px-2 py-2 overflow-hidden"
                >
                  <div className="flex flex-wrap gap-1">
                    {emojis.map((e) => (
                      <button
                        key={e}
                        onClick={() => setInput((prev) => prev + e)}
                        className="w-7 h-7 flex items-center justify-center hover:bg-white/10 rounded-lg transition-colors text-sm"
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Input */}
            <div className="flex items-center gap-1.5 px-2 py-2 border-t border-[#2A2A45]/60">
              <button
                onClick={() => setShowEmoji(!showEmoji)}
                className={`p-1.5 rounded-lg transition-colors ${showEmoji ? "bg-[#F37020]/20 text-[#F37020]" : "text-white/40 hover:text-white/70 hover:bg-white/5"}`}
              >
                <Smile className="w-4 h-4" />
              </button>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Type a message..."
                className="flex-1 bg-[#1C1C32]/60 border border-[#2A2A45]/60 rounded-lg px-2.5 py-1.5 text-xs text-white placeholder-white/25 focus:outline-none focus:border-[#F37020]/50"
                maxLength={200}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim()}
                className="p-1.5 text-[#F37020] hover:bg-[#F37020]/10 rounded-lg transition-colors disabled:opacity-30"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ---- Room Info Panel (Top-Left) ----
export function RoomInfoOverlay({
  roomName,
  smallBlind,
  bigBlind,
  playerCount,
  maxPlayers,
  pot,
  phase,
}: {
  roomName: string
  smallBlind: number
  bigBlind: number
  playerCount: number
  maxPlayers: number
  pot: number
  phase: string
}) {
  const [collapsed, setCollapsed] = useState(false)

  return (
    <div className="absolute top-14 left-2 z-30" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <motion.div
        className="bg-[#121220]/85 border border-[#2A2A45] rounded-2xl backdrop-blur-md overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.3)]"
        layout
      >
        {/* Header - always visible */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center justify-between w-full px-3 py-2 hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Info className="w-3.5 h-3.5 text-[#F37020]" />
            <span className="text-white/90 text-xs font-bold uppercase tracking-wider">{roomName}</span>
          </div>
          {collapsed ? (
            <ChevronDown className="w-3.5 h-3.5 text-white/40" />
          ) : (
            <ChevronUp className="w-3.5 h-3.5 text-white/40" />
          )}
        </button>

        <AnimatePresence>
          {!collapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-3 pb-2.5 space-y-1.5">
                {/* Blind Level */}
                <div className="bg-[#1C1C32]/80 rounded-xl px-2.5 py-2 border border-[#2A2A45]/50">
                  <p className="text-[#6A6A8A] text-[10px] font-bold uppercase tracking-wider">Blind Level</p>
                  <p className="text-[#FFD700] text-lg font-black" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                    {smallBlind.toLocaleString()}/{bigBlind.toLocaleString()}
                  </p>
                </div>

                {/* Stats Row */}
                <div className="flex gap-1.5">
                  <div className="flex-1 bg-[#1C1C32]/80 rounded-xl px-2.5 py-1.5 border border-[#2A2A45]/50">
                    <p className="text-[#6A6A8A] text-[9px] font-bold uppercase">Players</p>
                    <p className="text-white/90 text-sm font-black" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      {playerCount}/{maxPlayers}
                    </p>
                  </div>
                  <div className="flex-1 bg-[#1C1C32]/80 rounded-xl px-2.5 py-1.5 border border-[#2A2A45]/50">
                    <p className="text-[#6A6A8A] text-[9px] font-bold uppercase">Pot</p>
                    <p className="text-[#FFD700] text-sm font-black" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      {pot.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Phase */}
                <div className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${
                    phase === "waiting" ? "bg-yellow-400" :
                    phase === "showdown" ? "bg-purple-400" :
                    "bg-[#32CD32]"
                  }`} />
                  <span className="text-white/50 text-[10px] font-bold uppercase">
                    {phase === "waiting" ? "Waiting" :
                     phase === "preflop" ? "Pre-Flop" :
                     phase === "flop" ? "Flop" :
                     phase === "turn" ? "Turn" :
                     phase === "river" ? "River" :
                     phase === "showdown" ? "Showdown" : phase}
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  )
}

// ---- Hand Rankings Panel (Top-Right) ----
// Mini card component for hand examples
function MiniCard({ rank, suit, highlight = false }: { rank: string; suit: "♠" | "♥" | "♦" | "♣"; highlight?: boolean }) {
  const isRed = suit === "♥" || suit === "♦"
  return (
    <span
      className={`inline-flex flex-col items-center justify-center rounded-[3px] border text-[9px] leading-none font-bold`}
      style={{
        width: 22, height: 30,
        fontFamily: "'IBM Plex Mono', monospace",
        background: highlight ? "linear-gradient(135deg, #FFD700, #F37020)" : "#1C1C32",
        borderColor: highlight ? "#FFD700" : "#2A2A45",
        color: highlight ? "#121220" : isRed ? "#ef4444" : "#e5e5e5",
        boxShadow: highlight ? "0 0 6px rgba(255,215,0,0.3)" : "none",
      }}
    >
      <span style={{ fontSize: 9, lineHeight: 1 }}>{rank}</span>
      <span style={{ fontSize: 8, lineHeight: 1, marginTop: 1 }}>{suit}</span>
    </span>
  )
}

// Cards for each hand example
function HandExample({ cards }: { cards: Array<{ rank: string; suit: "♠" | "♥" | "♦" | "♣"; highlight?: boolean }> }) {
  return (
    <div className="flex gap-[2px] mt-1">
      {cards.map((c, i) => (
        <MiniCard key={i} rank={c.rank} suit={c.suit} highlight={c.highlight} />
      ))}
    </div>
  )
}

const HAND_RANKINGS = [
  { name: "Royal Flush", desc: "A, K, Q, J, 10 of same suit",
    cards: [
      { rank: "A", suit: "♠" as const, highlight: true },
      { rank: "K", suit: "♠" as const, highlight: true },
      { rank: "Q", suit: "♠" as const, highlight: true },
      { rank: "J", suit: "♠" as const, highlight: true },
      { rank: "10", suit: "♠" as const, highlight: true },
    ] },
  { name: "Straight Flush", desc: "Five sequential cards, same suit",
    cards: [
      { rank: "9", suit: "♥" as const, highlight: true },
      { rank: "8", suit: "♥" as const, highlight: true },
      { rank: "7", suit: "♥" as const, highlight: true },
      { rank: "6", suit: "♥" as const, highlight: true },
      { rank: "5", suit: "♥" as const, highlight: true },
    ] },
  { name: "Four of a Kind", desc: "Four cards of same rank",
    cards: [
      { rank: "K", suit: "♠" as const, highlight: true },
      { rank: "K", suit: "♥" as const, highlight: true },
      { rank: "K", suit: "♦" as const, highlight: true },
      { rank: "K", suit: "♣" as const, highlight: true },
      { rank: "3", suit: "♠" as const },
    ] },
  { name: "Full House", desc: "Three of a kind + a pair",
    cards: [
      { rank: "J", suit: "♠" as const, highlight: true },
      { rank: "J", suit: "♥" as const, highlight: true },
      { rank: "J", suit: "♦" as const, highlight: true },
      { rank: "8", suit: "♣" as const, highlight: true },
      { rank: "8", suit: "♠" as const, highlight: true },
    ] },
  { name: "Flush", desc: "Five cards of same suit",
    cards: [
      { rank: "A", suit: "♦" as const, highlight: true },
      { rank: "J", suit: "♦" as const, highlight: true },
      { rank: "9", suit: "♦" as const, highlight: true },
      { rank: "6", suit: "♦" as const, highlight: true },
      { rank: "3", suit: "♦" as const, highlight: true },
    ] },
  { name: "Straight", desc: "Five sequential cards",
    cards: [
      { rank: "10", suit: "♠" as const, highlight: true },
      { rank: "9", suit: "♥" as const, highlight: true },
      { rank: "8", suit: "♦" as const, highlight: true },
      { rank: "7", suit: "♣" as const, highlight: true },
      { rank: "6", suit: "♠" as const, highlight: true },
    ] },
  { name: "Three of a Kind", desc: "Three cards of same rank",
    cards: [
      { rank: "Q", suit: "♠" as const, highlight: true },
      { rank: "Q", suit: "♥" as const, highlight: true },
      { rank: "Q", suit: "♦" as const, highlight: true },
      { rank: "9", suit: "♣" as const },
      { rank: "4", suit: "♠" as const },
    ] },
  { name: "Two Pair", desc: "Two different pairs",
    cards: [
      { rank: "A", suit: "♠" as const, highlight: true },
      { rank: "A", suit: "♥" as const, highlight: true },
      { rank: "7", suit: "♦" as const, highlight: true },
      { rank: "7", suit: "♣" as const, highlight: true },
      { rank: "2", suit: "♠" as const },
    ] },
  { name: "One Pair", desc: "Two cards of same rank",
    cards: [
      { rank: "10", suit: "♠" as const, highlight: true },
      { rank: "10", suit: "♥" as const, highlight: true },
      { rank: "K", suit: "♦" as const },
      { rank: "5", suit: "♣" as const },
      { rank: "3", suit: "♠" as const },
    ] },
  { name: "High Card", desc: "Highest card wins",
    cards: [
      { rank: "A", suit: "♠" as const, highlight: true },
      { rank: "J", suit: "♥" as const },
      { rank: "8", suit: "♦" as const },
      { rank: "5", suit: "♣" as const },
      { rank: "2", suit: "♠" as const },
    ] },
]

export function HandRankingsOverlay() {
  const [isOpen, setIsOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [isOpen])


  return (
    <div ref={panelRef} className="absolute top-14 right-2 z-30" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {/* Toggle Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-[#121220]/85 border border-[#2A2A45] rounded-xl backdrop-blur-md text-white/70 text-xs font-bold hover:text-white transition-colors ml-auto"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Trophy className="w-3.5 h-3.5 text-[#FFD700]" />
        Hand Rankings
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="mt-1.5 bg-[#121220]/90 border border-[#2A2A45] rounded-2xl backdrop-blur-md overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.4)] w-[300px]"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#2A2A45]/60">
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-[#FFD700]" />
                <span className="text-white/90 text-xs font-bold uppercase tracking-wider">Hand Rankings</span>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-3.5 h-3.5 text-white/50" />
              </button>
            </div>

            {/* Rankings List */}
            <div className="max-h-[400px] overflow-y-auto px-2 py-2 space-y-0.5 scrollbar-thin scrollbar-thumb-[#2A2A45] scrollbar-track-transparent">
              {HAND_RANKINGS.map((hand, i) => (
                <div
                  key={hand.name}
                  className="flex items-center gap-2.5 px-2.5 py-2 rounded-xl hover:bg-white/5 transition-colors"
                >
                  <span className="text-[#FFD700]/80 text-[10px] font-black w-4 text-right" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/90 text-xs font-bold">{hand.name}</p>
                    <p className="text-white/35 text-[10px]">{hand.desc}</p>
                    <HandExample cards={hand.cards} />
                  </div>
                </div>
              ))}
            </div>

            {/* Rules Footer */}
            <div className="px-3 py-2 border-t border-[#2A2A45]/60">
              <p className="text-white/25 text-[9px] text-center font-medium">
                Texas Hold'em • Best 5-card hand wins
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
